import { DBPlaylistType, DBSongType, UserGroupType, UserType } from 'types'
import { Redirect, Route, Switch, useLocation } from 'react-router-dom'
import { makeVar, useQuery, useReactiveVar } from '@apollo/client'
import styled, { keyframes } from 'styled-components'
import { useEffect, useRef } from 'react'

import AppLayout from 'layouts/AppLayout'
import { GET_LOGGED_USER } from 'api/graphql'
import Loader from 'components/App/Loader'
import { ROUTES } from 'const'
import { withTranslation } from 'react-i18next'

const Wrapper = styled.div`
  position: relative;
  color: ${(props) => props.theme.black};
`

const NoLayout = styled.div`
  overflow: hidden;
`

const LoadingScreen = styled.div`
  width: 100%;
  height: 100%;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: ${(props) => props.theme.primary};
`

const AnimationIn = keyframes`
  0% {
    opacity: 1;
    z-index: 1000
  }
  99% {
    opacity: 0;
    z-index: 1000
  }
  100% {
    opacity: 0;
    z-index: -1000
  }
`
const AfterLoginOverlay = styled.div`
  position: absolute;
  z-index: -1000;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: ${(props) => props.theme.primary};
  pointer-events: none;

  animation-name: ${AnimationIn};
  animation-timing-function: ease;
  animation-duration: 0.4s;
  animation-fill-mode: forwards;
`

//
// LOCAL STORAGE
//
export const LOGGED_USER_VAR = makeVar<UserType>(null)
export const USER_SONG_LIST_VAR = makeVar<DBSongType[]>(null)
export const USER_LIST_VAR = makeVar<UserType[]>(null)
export const USER_GROUP_LIST_VAR = makeVar<UserGroupType[]>(null)
export const USER_PLAYLIST_LIST_VAR = makeVar<DBPlaylistType[]>(null)

/**
 *
 *
 * COMPONENT
 *
 *
 */
const App = ({ i18n }) => {
  const location = useLocation()
  const slugBeforeRedirect = useRef()
  const paramsBeforeRedirect = useRef()

  const loggedUser = useReactiveVar(LOGGED_USER_VAR)

  //
  // LOGGED USER
  //
  const { loading: loggedUserLoading, data: loggedUserData } =
    useQuery(GET_LOGGED_USER)

  useEffect(() => {
    if (loggedUserData?.getLoggedUser) {
      LOGGED_USER_VAR(loggedUserData?.getLoggedUser)
      const lang = loggedUserData?.getLoggedUser?.languageCode || 'en'
      i18n.changeLanguage(lang)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loggedUserData])

  //
  // RENDER
  //
  const renderApp = () => {
    //
    // LOGIN REDIRECT WITH MEMORY
    //
    const currentSlug = location.pathname
    const isLoginPage = currentSlug === ROUTES.login.slug
    const noLoggedUser = !(loggedUser?.id >= 0)
    const doRedirectToLogin = loggedUserData && noLoggedUser && !isLoginPage
    const doRedirectFromLogin = loggedUser?.id >= 0 && isLoginPage

    if (doRedirectToLogin) {
      slugBeforeRedirect.current = currentSlug
      paramsBeforeRedirect.current = location.search
    }

    if (i18n.isInitialized && !loggedUserLoading) {
      if (doRedirectToLogin) {
        return <Redirect to={ROUTES.login.slug} />
      }
      if (doRedirectFromLogin) {
        return (
          <Redirect
            to={
              slugBeforeRedirect.current
                ? `${slugBeforeRedirect.current}${
                    paramsBeforeRedirect.current || ''
                  }`
                : ROUTES.home.slug
            }
          />
        )
      }
      return (
        <>
          <Switch>
            {/* AUTO GENERATED ROUTES */}
            {Object.values(ROUTES).map((route, i) =>
              route.noLayout ? (
                <Route path={route.slug} key={i}>
                  <NoLayout>{route.page}</NoLayout>
                </Route>
              ) : (
                <Route path={route.slug} key={i}>
                  <AppLayout>{route.page}</AppLayout>
                </Route>
              )
            )}
          </Switch>

          {!isLoginPage && (
            <>
              <AfterLoginOverlay />
            </>
          )}
        </>
      )
    }
    return null
  }

  return (
    <Wrapper>
      {renderApp() || (
        <LoadingScreen>
          <Loader inverted />
        </LoadingScreen>
      )}
    </Wrapper>
  )
}

export default withTranslation()(App)
